import { useCallback, useEffect, useRef, useState } from 'react';
import { get } from '../request';

// 受控意图词表中文名映射（统一展示源）：
// GET /intent/vocab-names 全员可读（含停用词条），供话术库/坐席工作台/工单分类等所有展示点
// 把英文 intent code 统一渲染为中文名（ai_intent_vocab.name）。模块级缓存，全页共享一次请求。
// 历史绑定停用意图仍能解析中文名；未命中返回 code 兜底。
// 失败语义：**不缓存失败**。请求失败时既不写空映射、也不留着那个 promise，
// 下次调用（页面重进 / useIntentNames 返回的重载入口）自然重试——否则一次 500 就让整个 SPA 会话
// 的意图全显示英文 code，且页面内没有任何自愈入口（只能整页刷新）。

let loaded: Record<string, string> | null = null;
let promise: Promise<Record<string, string>> | null = null;

function ensure(): Promise<Record<string, string>> {
  if (loaded) return Promise.resolve(loaded);
  if (!promise) {
    promise = get<Record<string, string>>('/intent/vocab-names')
      .then((m) => {
        loaded = m || {};
        return loaded;
      })
      .catch((e: unknown) => {
        // 关键：失败要复位 promise（并把 loaded 留在 null）。写 `loaded = {}` 会让它变成真值，
        // getIntentName 从此永远命中 `loaded?.[code] || code` 的兜底分支，再也不会去拉第二次。
        promise = null;
        throw e;
      });
  }
  return promise;
}

/** 失效缓存：下次 ensure 重新拉取（供页面「重新加载」入口 / 词表刚被改动时调用） */
export function invalidateIntentNames(): void {
  loaded = null;
  promise = null;
}

/** 同步取意图中文名；code 为空返回 ''，缓存未命中（含尚未加载/加载失败）返回 code 兜底 */
export function getIntentName(code?: string | null): string {
  if (!code) return '';
  return loaded?.[code] || code;
}

/** 预拉取词表名映射（已加载直接 resolve；失败不抛错——失败态不落缓存，下次调用会重试） */
export function ensureIntentNames(): Promise<void> {
  return ensure().then(
    () => undefined,
    () => undefined
  );
}

/**
 * React hook：挂载即拉取词表名映射，就绪后触发一次重渲染（把 code 替换为中文名）。
 * 返回「重新加载」回调（失效缓存 + 重拉 + 重渲染）：首次失败后页面可就地重试，不必整页刷新。
 */
export function useIntentNames(): () => void {
  const [, setTick] = useState(0);
  // 存活标记：拉取的 promise 生命周期比一次挂载长（失败后还有重试路径），卸载后不得再 setState
  const aliveRef = useRef(true);
  const load = useCallback(() => {
    ensure()
      .then(() => {
        if (aliveRef.current) setTick((t) => t + 1);
      })
      .catch(() => { /* 失败保持 code 兜底：loaded/promise 已复位，下次调用会重试 */ });
  }, []);
  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);
  // 对外给出「重新加载」回调：先失效缓存再重拉（页面「加载失败重试」入口用，不必整页刷新）
  const reload = useCallback(() => {
    invalidateIntentNames();
    load();
  }, [load]);
  return reload;
}

/** 风险等级中文标签（DB 存 low|medium|high|fatal，统一展示中文） */
export const RISK_LABEL: Record<string, string> = {
  low: '低风险',
  medium: '中风险',
  high: '高风险',
  fatal: '致命',
};

/** 风险等级标签色（与话术库列表同口径） */
export const RISK_COLOR: Record<string, string> = {
  low: 'gray',
  medium: 'orange',
  high: 'red',
  fatal: 'magenta',
};

/** 风险等级 → 工作台 wb-chip 语义色（红=高危/致命/严重，中=橙，其余灰）。
 * 与 RISK_COLOR 的区别：后者是 Arco Tag 色板（fatal=magenta），wb-chip 无该色。 */
export function riskChipColor(v?: string): string {
  return v === 'fatal' || v === 'high' || v === 'critical' ? 'red' : v === 'medium' ? 'orange' : 'gray';
}
